import { useState } from 'react';

export function Contact() {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission - for now just log and close
    console.log('Form submitted:', formData);
    setShowForm(false);
    // Reset form
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Section Title */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-600 mb-8">Contact Us</h2>
          <div className="w-24 h-px bg-blue-600 mx-auto"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Contact Information */}
          <div className="space-y-8">
            {/* 42B Information */}
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">42B</h3>
              <div className="space-y-2 text-gray-600">
                <p>
                  <a href="tel:8606159068" className="hover:text-blue-600 transition-colors">
                    860-615-9068
                  </a>
                </p>
                <p>
                  <a href="mailto:goodspeedairport@gmail.com" className="hover:text-blue-600 transition-colors">
                    goodspeedairport@gmail.com
                  </a>
                </p>
              </div>
            </div>

            {/* Airport Information */}
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Goodspeed Airport</h3>
              <div className="space-y-2 text-gray-600">
                <p>15 Lumberyard Road, East Haddam, Connecticut 06423, United States</p>
                <p>
                  <a href="tel:8606159068" className="hover:text-blue-600 transition-colors">
                    860-615-9068
                  </a>
                </p>
              </div>
            </div>

            {/* Contact Form Button */}
            <div>
              {!showForm ? (
                <button
                  onClick={() => setShowForm(true)}
                  className="bg-blue-600 text-white px-6 py-3 rounded hover:bg-blue-700 transition-colors font-medium"
                >
                  Drop us a line!
                </button>
              ) : (
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="text-xl font-bold text-gray-800 mb-4">Drop us a line!</h4>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                          Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email*
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Message"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="text-xs text-gray-500 mb-4">
                      This site is protected by reCAPTCHA and the Google{' '}
                      <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline">
                        Privacy Policy
                      </a>{' '}
                      and{' '}
                      <a href="https://policies.google.com/terms" className="text-blue-600 hover:underline">
                        Terms of Service
                      </a>{' '}
                      apply.
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition-colors font-medium"
                      >
                        Send
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowForm(false)}
                        className="text-gray-600 px-6 py-2 hover:text-gray-800 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Map */}
          <div className="h-96 lg:h-full min-h-[400px]">
            <div className="w-full h-full bg-green-100 rounded-lg overflow-hidden relative">
              {/* Embedded Google Map */}
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2982.123456789!2d-72.456521!3d41.445952!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e65a1b8c9d4567%3A0x1234567890abcdef!2s15%20Lumberyard%20Rd%2C%20East%20Haddam%2C%20CT%2006423!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Goodspeed Airport Location"
                className="rounded-lg"
              />
              
              {/* Get Directions Button */}
              <div className="absolute bottom-4 left-4">
                <a
                  href="https://www.google.com/maps/dir//15+Lumberyard+Rd,+East+Haddam,+CT+06423"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-gray-800 px-4 py-2 rounded shadow-md hover:bg-gray-50 transition-colors text-sm font-medium inline-flex items-center gap-2"
                >
                  <span>📍</span>
                  Get directions
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}